import { Anchor, Box, CSSObject, Divider, Modal, Stack, Tabs } from '@mantine/core';
import { useState } from 'react';
import Project from 'src/entities/project';
import ProjectRole, { ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import GuestHash from '@projects/models/guestHash';
import EmailInvite from '@projectMembers/components/EmailInvite/EmailInvite';
import ShareInviteLink from '@projectMembers/components/ShareInviteLink/ShareInviteLink';
import ProjectMembersList from '@projectMembers/components/ProjectMembersList/ProjectMembersList';
import { textVariants } from '@ui/tokens/typography/typography';
import OverviewPermissionTooltip from 'src/new_arch/modules/projectView/components/OverviewPermissionTooltip/OverviewPermissionTooltip';
import { UPGRADE_REQUEST_SOURCES } from 'src/new_arch/modules/projectView/components/OverviewPermissionTooltip/upgradeRequestSources';

import Icon from '@ui/components/Icon/Icon';
import ViewOnlyLink from './ViewOnlyLink/ViewOnlyLink';
import useShareProjectModal from './useShareProjectModal';
import ManageReviewerRole from './ManageReviewerRole/ManageReviewerRole';

const LEARN_MORE_URL = 'https://support.verybusy.io/en/articles/9650222-roles-within-verybusy-io#h_c08e6478ea';

export type ShareProjectModalProps = {
	project: Project;
	onClose: () => void;
};

const buildTabStyles = () => ( {
	root: {
		background: 'transparent'
	},
	tab: {
		'padding': '12px 4px 8px',
		'borderColor': 'var(--color-outline-light)',
		'borderWidth': 0,
		...( textVariants[ 'body-bold' ] as CSSObject ),
		'&:hover': {
			backgroundColor: 'transparent',
			color: 'var(--color-text-secondary)',
			borderColor: 'var(--color-outline-default)'
		},
		'&[aria-selected="false"]': {
			'fontWeight': 400,
			'color': 'var(--color-text-secondary)',
			'&:hover': { color: 'var(--color-text-secondary)' }
		},
		'&[aria-selected="true"]': {
			color: 'var(--color-text-primary)',
			borderWidth: '0 0 2px',
			borderColor: 'var(--color-primary-base)'
		}
	},
	tabsList: {
		padding: '0 16px',
		borderBottom: '1px solid var(--color-outline-light)',
		backgroundColor: 'var(--color-bg-container)'
	},
	panel: { padding: '0', minHeight: '220px', background: 'transparent' }
} );

const peoplePanelStyle = { maxHeight: '300px', overflowY: 'auto' } as const;

const SUBMODALS_STACK_ORDER = 2;

const ShareProjectModal = ( { project, onClose }: ShareProjectModalProps ) => {
	const {
		opened,
		membersCount,
		currentUserID,
		showManageReviewerRole,
		modalStyles
	} = useShareProjectModal( { project, onClose } );

	const tabStyles = buildTabStyles();

	const [ emailInviteSelectedRole, setEmailInviteSelectedRole ] = useState<ProjectRole>();
	const [ reviewerLinkEnabled, setReviewerLinkEnabled ] = useState<boolean>( false );

	const handleEmailInviteRoleChange = ( role: ProjectRole ) => {
		setEmailInviteSelectedRole( role );
	};

	const handleInviteLinkChange = ( guestHash: GuestHash ) => {
		if ( guestHash.roleType === ProjectMemberRoleType.ProjectReviewer ) {
			setReviewerLinkEnabled( guestHash.enabled );
		}
	};

	const isReviewerRoleSelected = emailInviteSelectedRole?.id === ProjectMemberRoleType.ProjectReviewer;

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={(
				<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					{`Share ${project.name}`}
					<Anchor href={LEARN_MORE_URL} target="_blank" style={{ display: 'flex', lineHeight: 1 }}>
						<Icon icon="info_outlined" size={14} color="var(--color-text-secondary)" />
					</Anchor>
				</div>
			)}
			className="ShareProjectModal"
			styles={modalStyles}
			size={466}
			closeOnEscape={false}
		>
			<Stack spacing={0} justify="space-between">
				<Tabs defaultValue="share" styles={tabStyles}>
					<Tabs.List grow position="center">
						<Tabs.Tab value="share">
							Share
						</Tabs.Tab>
						<Tabs.Tab value="people">
							People {membersCount ? `(${membersCount})` : ''}
						</Tabs.Tab>
					</Tabs.List>

					<Tabs.Panel value="share">
						<Stack bg="var(--color-bg-container)" p={16} pb={8} spacing={12}>
							<EmailInvite
								project={project}
								addGroupModalStackOrder={SUBMODALS_STACK_ORDER}
								onRoleSelectionChange={handleEmailInviteRoleChange}
							/>
							<Divider color="var(--color-outline-default)" />
							<ShareInviteLink
								project={project}
								onInviteLinkChange={handleInviteLinkChange}
							/>
							{showManageReviewerRole ? (
								<ManageReviewerRole
									project={project}
									modalsStackOrder={SUBMODALS_STACK_ORDER}
									showCustomizePermissions={reviewerLinkEnabled || isReviewerRoleSelected}
								/>
							) : (
								<div style={{ alignSelf: 'flex-start' }}>
									<OverviewPermissionTooltip
										projectID={project.id}
										source={UPGRADE_REQUEST_SOURCES.MANAGER_REVIEWER_ROLE}
									>
										<ManageReviewerRole
											project={project}
											modalsStackOrder={SUBMODALS_STACK_ORDER}
											disabled
										/>
									</OverviewPermissionTooltip>
								</div>
							)}
							<Divider color="var(--color-outline-default)" />
							<ViewOnlyLink project={project} />
						</Stack>
					</Tabs.Panel>
					<Tabs.Panel value="people">
						<Stack bg="var(--color-bg-container)" spacing={0}>
							<Box style={peoplePanelStyle} px={16} py={8}>
								<ProjectMembersList
									project={project}
									currentUserRole={project.currentUserRole}
									currentUserID={currentUserID}
								/>
							</Box>
						</Stack>
					</Tabs.Panel>
				</Tabs>
			</Stack>
		</Modal>
	);
};

export default ShareProjectModal;
